import { redirect } from '@tanstack/react-router'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  brokerCompareQueryOptions,
  brokerListQueryOptions,
  stocksInCommonQueryOptions,
} from '@/features/brokers/api/queries'
import { readLastBroker } from '@/features/brokers/lib/lastUsed'
import type { RouterContext } from '@/routes/layouts/root'
import { COMPARE_BROKER_LIST_QUERY } from '@/screens/brokers/lib/compareBrokerList'
import { compareSearchSchema } from '@/screens/brokers/search'

/**
 * The two houses an arrival with no picks opens on (the mock opens on two): the member's last-used
 * house while it is still in today's directory, then the busiest by turnover — or the busiest two
 * when there is no last-used one, or it is the busiest itself. Fewer than two in the directory
 * gives none, and the page draws its empty state.
 */
export const defaultComparePicks = (
  directory: readonly string[],
  lastUsed: string | null,
): string[] | undefined => {
  const first = lastUsed !== null && directory.includes(lastUsed) ? lastUsed : undefined
  const rest = directory.filter((code) => code !== first)
  const picks = first === undefined ? rest.slice(0, 2) : [first, ...rest.slice(0, 1)]
  return picks.length === 2 ? picks : undefined
}

/**
 * The `/brokers/compare` loader.
 *
 * ARRIVING WITH NO PICKS (`cause === 'enter'`, no `codes`): awaits today's directory (sorted by
 * turnover, the picker's own list) and redirects to the two default picks — see
 * `defaultComparePicks`. Only on ENTER: a member who removes every card stays on this route
 * (`cause === 'stay'`) and gets the empty state, not a fresh pair. A failed directory read skips
 * the redirect rather than failing the page; the empty state then draws its own retryable picker.
 *
 * Otherwise NO MAIN DATA, and nothing awaited. SECONDARY, prefetched only on a real navigation
 * (docs/route-loading.md, "Secondary reads"): the directory, and — when the URL carries 2-4 codes
 * — the comparison and the stocks-in-common read, so "The numbers" and the default sub-tab draw
 * from cache. Each pick's own page read (logo, style) and full stock list are left to the cards:
 * they are per-pick fan-outs, already self-skeletoned.
 */
export async function loadBrokersCompare({
  context: { queryClient },
  location,
  preload,
  cause,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
  cause: 'preload' | 'enter' | 'stay'
}): Promise<void> {
  if (preload) return
  const search = compareSearchSchema.parse(location.search)
  const { codes } = search
  if (codes === undefined && cause === 'enter') {
    let picks: string[] | undefined
    try {
      const page = await ensureRouteData(
        queryClient,
        brokerListQueryOptions(COMPARE_BROKER_LIST_QUERY),
      )
      picks = defaultComparePicks(
        page.rows.map((row) => row.code),
        readLastBroker(),
      )
    } catch {
      // A broken directory read costs the member the default pair, not the page.
      picks = undefined
    }
    if (picks !== undefined) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/brokers/compare', search: { ...search, codes: picks }, replace: true })
    }
    return
  }
  prefetchRouteData(queryClient, brokerListQueryOptions(COMPARE_BROKER_LIST_QUERY))
  if (codes !== undefined && codes.length >= 2) {
    prefetchRouteData(queryClient, brokerCompareQueryOptions(codes, 'today'))
    prefetchRouteData(queryClient, stocksInCommonQueryOptions(codes, 'today'))
  }
}
